import { motion } from "framer-motion";
import { PlayCircle, Users, Sparkles, Layers, Quote, ExternalLink } from "lucide-react";
import CaseStudyLayout, { Bridge } from "./CaseStudyLayout";
import BrowserFrame from "../components/BrowserFrame";
import { projects } from "../lib/data";
import { EASE_OUT, fadeUp, fadeUpSubtle, popIn, staggerParent } from "../lib/motion";
import {
  PersonaPortrait,
  IllustratedDisc,
  IconTrendUp,
  IconTrendDown,
  IconShield,
  IconSparkles,
  IconClockLg,
  IconStackLg,
} from "../components/Illustrations";

const chapters = [
  { id: "cs-context", label: "Context", idx: 0 },
  { id: "cs-role", label: "Role", idx: 1 },
  { id: "cs-research", label: "Research", idx: 2 },
  { id: "cs-process", label: "Process", idx: 3 },
  { id: "cs-solution", label: "Solution", idx: 4 },
  { id: "cs-impact", label: "Impact", idx: 5 },
  { id: "cs-reflection", label: "Reflection", idx: 6 },
];

// ───── Editorial building blocks ────────────────────────────

const Chapter = ({ n, label }) => (
  <div className="flex items-center gap-4">
    <div className="chapter-num text-5xl md:text-6xl">{n}</div>
    <div className="section-label">{label}</div>
  </div>
);

const Block = ({ id, idx, n, label, title, kicker, children, sticky }) => (
  <section
    id={id}
    data-chapter-idx={idx}
    className="py-16 md:py-32 border-t border-[color:var(--nn-border)] relative"
  >
    <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-12 gap-8 md:gap-14">
      <div className="md:col-span-4">
        <div className={sticky ? "md:sticky md:top-32" : ""}>
          <div className="mb-8">
            <Chapter n={n} label={label} />
          </div>
          {title && (
            <motion.h2
              {...fadeUp(0)}
              className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-5"
            >
              {title}
            </motion.h2>
          )}
          {kicker && (
            <motion.p
              {...fadeUp(0.06)}
              className="text-sm md:text-base text-[color:var(--nn-muted)] leading-relaxed max-w-sm"
            >
              {kicker}
            </motion.p>
          )}
        </div>
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  </section>
);

const FeatureCard = ({ icon: Icon, tag, title, children, delay = 0 }) => (
  <motion.div
    {...fadeUpSubtle(delay)}
    className="p-6 md:p-7 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60 backdrop-blur relative overflow-hidden group"
  >
    <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[color:var(--nn-accent)]/10 blur-3xl group-hover:bg-[color:var(--nn-accent)]/20 transition-colors duration-300" />
    <div className="relative">
      <div className="flex items-center gap-2 font-mono text-[10px] text-[color:var(--nn-accent-text)] uppercase tracking-widest mb-4">
        {Icon && <Icon size={12} />} {tag}
      </div>
      <div className="font-display text-xl md:text-2xl mb-3 leading-tight">{title}</div>
      <div className="text-sm text-[color:var(--nn-muted)] leading-relaxed">{children}</div>
    </div>
  </motion.div>
);

const DemoBlock = ({ videoUrl, embedUrl, poster, label = "Live product walkthrough", caption }) => {
  const isExternal = Boolean(embedUrl) && !videoUrl;

  return (
    <motion.div {...fadeUp(0)} className="spotlight-frame relative bg-[color:var(--nn-surface)]">
      <div className="relative aspect-video bg-[color:var(--nn-elev)] group">
        {isExternal ? (
          <a
            href={embedUrl}
            target="_blank"
            rel="noreferrer"
            data-cursor="dragging"
            data-cursor-label="launch prototype"
            data-testid="demo-launch-prototype"
            className="absolute inset-0 block"
          >
            {poster && (
              <img
                src={poster}
                alt="Lumina Editor — FigmaMake prototype preview"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.02]"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/80 via-[#0d0d0d]/20 to-transparent" />
            {/* Center CTA */}
            <div className="absolute inset-0 grid place-items-center">
              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
                className="flex items-center gap-3 pl-4 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium shadow-2xl group-hover:gap-5 transition-all duration-300"
              >
                <span className="text-base md:text-lg">Launch interactive prototype</span>
                <span className="w-11 h-11 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)] group-hover:rotate-45 transition-transform duration-500">
                  <ExternalLink size={18} />
                </span>
              </motion.div>
            </div>
            {/* Corner meta */}
            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur border border-white/15">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-accent)] animate-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-white">
                FigmaMake · Live prototype
              </span>
            </div>
          </a>
        ) : videoUrl ? (
          <video src={videoUrl} poster={poster} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
        ) : (
          <>
            {poster && (
              <img src={poster} alt="Lumina Editor — demo poster" className="absolute inset-0 w-full h-full object-cover object-top opacity-90" loading="lazy" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
            <div className="absolute inset-0 grid place-items-center">
              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
                className="flex items-center gap-3 pl-3 pr-5 py-3 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] shadow-2xl"
              >
                <PlayCircle size={22} />
                <span className="font-mono text-[11px] uppercase tracking-widest">Demo video · coming soon</span>
              </motion.div>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-4 px-5 py-3 bg-[color:var(--nn-elev)]/80 border-t border-[color:var(--nn-border)]">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-accent)] animate-pulse" />
          {label}
        </div>
        {caption && (
          <div className="hidden md:block font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">{caption}</div>
        )}
      </div>
    </motion.div>
  );
};

// ───── Illustrated persona quote card ─────────────────────────

const VoiceCard = ({ seed, tone, name, role, quote, index, delay = 0 }) => (
  <motion.div
    {...popIn(delay)}
    className="relative rounded-[22px] border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/70 backdrop-blur-md p-6 md:p-7 flex flex-col h-full overflow-hidden group hover:border-[color:var(--nn-accent)]/40 transition-colors duration-300"
  >
    <div className="flex items-start justify-between gap-3 mb-5">
      <IllustratedDisc tone={tone} size={84}>
        <div className="w-full h-full p-1">
          <PersonaPortrait seed={seed} alt={`${name} — illustrated portrait`} size={80} />
        </div>
      </IllustratedDisc>
      <Quote size={20} className="text-[color:var(--nn-accent)] shrink-0 opacity-70" />
    </div>
    <blockquote className="font-serif-i text-lg md:text-xl leading-snug text-[color:var(--nn-text)] mb-6 flex-1">
      "{quote}"
    </blockquote>
    <div className="pt-4 border-t border-[color:var(--nn-border)]">
      <div className="font-display text-base leading-tight">{name}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mt-1">{role}</div>
    </div>
    <div className="absolute bottom-4 right-5 chapter-num text-2xl opacity-70">{index}</div>
  </motion.div>
);

// ───── Illustrated metric tile ───────────────────────────────

const MetricCard = ({ Icon, tone, value, label, sub, delay = 0 }) => (
  <motion.div
    {...popIn(delay)}
    className="rounded-2xl bg-[color:var(--nn-surface)]/70 backdrop-blur border border-[color:var(--nn-border)] p-5 md:p-6 flex flex-col gap-4 hover:border-[color:var(--nn-accent)]/40 transition-colors duration-300"
  >
    <IllustratedDisc tone={tone} size={56}>
      <Icon size={26} strokeWidth={1.7} />
    </IllustratedDisc>
    <div>
      <div className="font-display text-4xl md:text-5xl text-[color:var(--nn-text)] leading-none tracking-tight">
        {value}
      </div>
      <div className="font-display text-sm md:text-base mt-2 text-[color:var(--nn-text)]/85">{label}</div>
      {sub && (
        <div className="mt-2 text-xs text-[color:var(--nn-muted)] leading-relaxed">{sub}</div>
      )}
    </div>
  </motion.div>
);

// ───── Case study ────────────────────────────────────────────

const YujaLumina = () => {
  const project = projects.find((p) => p.slug === "yuja-lumina");

  return (
    <CaseStudyLayout project={project} chapters={chapters}>
      {/* 01 · Context & Problem */}
      <Block
        id="cs-context"
        idx={0}
        n="01"
        label="Context & Problem"
        sticky
        title={<>Every instructor. One dense <span className="font-serif-i text-[color:var(--nn-muted)]">editor.</span></>}
        kicker="YuJa Lumina powers lecture recording and WCAG-compliant video for 1,000+ institutions. The legacy editor served no one particularly well."
      >
        <motion.div {...fadeUp(0)} className="space-y-4 text-lg leading-relaxed text-[color:var(--nn-text)]/85 mb-10 max-w-2xl">
          <p>
            First-time TAs opened the exact same surface as a senior video producer. Enhanced
            Audio, Auto Captions, and Chapters — features quietly shipping in the background —
            were nearly undiscoverable. Every action lived one panel deep.
          </p>
          <p className="text-[color:var(--nn-muted)]">
            Instructors were burning ten minutes on tasks that should have been muscle memory,
            and none of them trusted the undo.
          </p>
        </motion.div>

        <div className="spotlight-frame">
          <BrowserFrame
            src={project.assets.oldEditor}
            alt="Legacy Lumina editor — dense single-surface"
            ratio="16/10"
            url="lumina.legacy/editor"
            background="bg-gradient-to-br from-[#0d0d0d] via-[#141414] to-[#0d0d0d]"
            testid="asset-old-editor"
            className="!border-0 !shadow-none"
          />
        </div>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
          <span className="text-[color:var(--nn-accent-text)]">▚</span> Legacy editor — one surface for every user. AI hidden two clicks deep.
        </div>
      </Block>

      <Bridge label="my role" />

      {/* 02 · Role & Team */}
      <Block
        id="cs-role"
        idx={1}
        n="02"
        label="Role & Team"
        sticky
        title={<>Lead designer. <span className="font-serif-i text-[color:var(--nn-muted)]">Small team.</span> Six weeks.</>}
        kicker="Owned strategy through handoff. Partnered daily with a product manager, two front-end engineers, and an accessibility specialist."
      >
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <FeatureCard icon={Users} tag="MY ROLE" title="Lead UI/UX Designer" delay={0}>
            Strategy, research synthesis, IA, hi-fi UI, motion, WCAG audit, and engineering handoff.
          </FeatureCard>
          <FeatureCard icon={Layers} tag="TEAM" title="4 people · 6 weeks" delay={0.05}>
            1 Product Manager · 2 Front-end Engineers · 1 A11y Specialist. Weekly demos with the YuJa leadership team.
          </FeatureCard>
          <FeatureCard icon={Sparkles} tag="STACK" title="FigmaMake + Design System" delay={0.1}>
            Designed against the 2026 tokens system. Prototyped in FigmaMake for live team review.
          </FeatureCard>
        </div>

        <motion.div {...fadeUp(0)} className="p-6 md:p-8 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 backdrop-blur">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">Ownership map</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            {[
              ["Research & audit", "Owned"],
              ["Information architecture", "Owned"],
              ["UI & motion design", "Owned"],
              ["Accessibility pass", "Co-led with a11y specialist"],
              ["Prototype (FigmaMake)", "Owned"],
              ["Engineering handoff", "Owned"],
              ["Product strategy", "Co-led with PM"],
              ["Roll-out & QA", "Supported"],
            ].map(([task, own]) => (
              <div key={task} className="flex items-center justify-between gap-4 border-b border-dashed border-[color:var(--nn-border)] pb-2">
                <span className="text-[color:var(--nn-text)]/85">{task}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)]">{own}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </Block>

      <Bridge label="voices from research" />

      {/* 03 · Research & Framing — with illustrated voice cards */}
      <Block
        id="cs-research"
        idx={2}
        n="03"
        label="Research & Framing"
        sticky
        title={<>What instructors <span className="font-serif-i text-[color:var(--nn-muted)]">actually</span> do.</>}
        kicker="A product audit across six institutions, competitive teardowns of CapCut/Descript/iMovie, and task-frequency mapping against real telemetry."
      >
        {/* Voices — illustrated persona card row */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-8 grid place-items-center rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)]">
              <Quote size={14} />
            </span>
            <div className="font-display text-2xl md:text-3xl">Voices from Research</div>
          </div>
          <motion.div {...staggerParent(0.06)} className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <VoiceCard
              seed="aarti-instructor-nn"
              tone="lime"
              index="01"
              name="Aarti, 42"
              role="Post-secondary Instructor"
              quote="I never touch anything I don't need to. I don't know what undo will do."
              delay={0}
            />
            <VoiceCard
              seed="sam-ta-media-nn"
              tone="blue"
              index="02"
              name="Sam, 27"
              role="TA · Media Studies"
              quote="Where are the captions? I click around for ten minutes every single Monday."
              delay={0.05}
            />
            <VoiceCard
              seed="malik-producer-nn"
              tone="peach"
              index="03"
              name="Malik, 51"
              role="Senior Video Producer"
              quote="Give me every panel. Just make the AI features actually say they're AI."
              delay={0.1}
            />
            <VoiceCard
              seed="riya-a11y-nn"
              tone="pink"
              index="04"
              name="Riya, 34"
              role="A11y Coordinator"
              quote="WCAG can't be a plugin. It has to be the base state — in both themes."
              delay={0.15}
            />
          </motion.div>
        </div>

        {/* Audit-driven core tasks — tidy grid */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
              <span className="text-[color:var(--nn-accent-text)]">▚</span> Task frequency · 6-institution audit
            </div>
          </div>
          <motion.div {...staggerParent(0.05)} className="grid md:grid-cols-2 gap-4">
            {[
              { tag: "01 · CAPTIONS", h: "Most-frequent task", p: "Across every persona, captions dominated session logs." },
              { tag: "02 · UPLOAD", h: "Multi-stream captures", p: "Classroom cameras produced a flurry of files needing tidy-up." },
              { tag: "03 · SPLIT & TRIM", h: "The fundamental verb", p: "Editing at its most basic — cut, discard, re-join." },
              { tag: "04 · ANNOTATIONS", h: "Frame-by-frame callouts", p: "Lecture markup ran a distant fourth — but felt essential." },
            ].map((c, i) => (
              <FeatureCard key={c.tag} tag={c.tag} title={c.h} delay={i * 0.05}>
                {c.p}
              </FeatureCard>
            ))}
          </motion.div>
        </div>

        {/* Framing quote — dedicated moment */}
        <motion.div
          {...fadeUp(0)}
          className="p-6 md:p-10 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[color:var(--nn-accent)]/20 blur-3xl" />
          </div>
          <div className="relative">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
              Framing note — week 1 synthesis
            </div>
            <blockquote className="font-serif-i text-2xl md:text-4xl leading-snug text-[color:var(--nn-text)] max-w-3xl">
              "Ninety percent of instructors touch four tasks. So why do all
              <span className="italic text-[color:var(--nn-accent-text)]"> ninety percent </span>
              open the ten-percent surface?"
            </blockquote>
          </div>
        </motion.div>
      </Block>

      <Bridge label="process & decisions" />

      {/* 04 · Process & Key Decisions */}
      <Block
        id="cs-process"
        idx={3}
        n="04"
        label="Process & Key Decisions"
        sticky
        title={<>Five moves that <span className="font-serif-i text-[color:var(--nn-muted)]">unlocked</span> the redesign.</>}
        kicker="FigmaMake accelerated the scaffold. Judgment shaped every trade-off — from AI labelling to the WCAG audit."
      >
        <div className="relative">
          <div className="absolute left-[9px] md:left-[11px] top-2 bottom-2 w-[2px] bg-[color:var(--nn-border)]" aria-hidden />
          {[
            { n: "01", h: "Audit before designing", p: "Task-frequency map across 6 institutions. Told us what to hide, not just what to show." },
            { n: "02", h: "Prompt the scaffold", p: "Prompted 5 zones · 6 panels · 2 modes in FigmaMake, wired to Design System 2026 tokens." },
            { n: "03", h: "Rewrote the AI copy", p: "Every AI-touched label was rewritten to be declared, not disguised. Trust before adoption." },
            { n: "04", h: "Ship two modes safely", p: "Simple ↔ Advanced toggle with zero data loss — the mode is a view, not a project state." },
            { n: "05", h: "Axe every screen", p: "Both themes tested with Axe DevTools until WCAG AA in dark + light. No exceptions." },
          ].map((s, i) => (
            <motion.div key={s.n} {...fadeUpSubtle(i * 0.05)} className="relative pl-10 md:pl-14 pb-8 md:pb-10 last:pb-0">
              <div className="absolute left-0 top-1 w-5 h-5 md:w-6 md:h-6 rounded-full bg-[color:var(--nn-accent)] grid place-items-center">
                <span className="font-mono text-[9px] text-[color:var(--nn-accent-ink)] font-bold">{s.n}</span>
              </div>
              <div className="font-display text-2xl md:text-3xl mb-2">{s.h}</div>
              <div className="text-sm md:text-base text-[color:var(--nn-muted)] leading-relaxed max-w-lg">{s.p}</div>
            </motion.div>
          ))}
        </div>

        <motion.blockquote {...fadeUp(0)} className="mt-14 font-serif-i text-2xl md:text-4xl leading-tight max-w-3xl border-l-2 border-[color:var(--nn-accent)] pl-6">
          "AI accelerated the build. Judgment made it trustworthy."
        </motion.blockquote>
      </Block>

      <Bridge label="the solution" />

      {/* 05 · The Solution — screenshots only, no sticky notes mixed in */}
      <Block
        id="cs-solution"
        idx={4}
        n="05"
        label="The Solution"
        sticky
        title={<>Two modes. Five zones. <span className="font-serif-i text-[color:var(--nn-muted)]">One</span> editor.</>}
        kicker="Simple mode surfaces only the four validated tasks. Advanced mode layers in overlays, audio, chapters, and navigation — without ever changing the project."
      >
        <div className="mb-10">
          <DemoBlock
            embedUrl="https://www.figma.com/make/NMsBiBCS9NKu3n0Jlfge2s/Video-Editor--2026?fullscreen=1&t=JUMc4NZLtFCDjBAr-1&code-node-id=0-9"
            poster={project.assets.simpleMode}
            label="Interactive · FigmaMake prototype"
            caption="Click to open — Simple ↔ Advanced"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="spotlight-frame">
            <BrowserFrame src={project.assets.simpleMode} alt="Simple mode — only the 4 core tasks" ratio="16/10" url="lumina.app/editor?mode=simple" testid="asset-simple-mode" className="!border-0 !shadow-none" />
          </div>
          <div className="spotlight-frame">
            <BrowserFrame src={project.assets.advancedMode} alt="Advanced mode — full editing surface" ratio="16/10" url="lumina.app/editor?mode=advanced" background="bg-gradient-to-br from-[#0d0d0d] via-[#151233] to-[#0d0d0d]" testid="asset-advanced-mode" className="!border-0 !shadow-none" />
          </div>
          <div className="spotlight-frame md:col-span-2">
            <BrowserFrame src={project.assets.history} alt="Named History panel — timestamped, reversible edits" ratio="21/9" url="lumina.app/editor?panel=history" background="bg-gradient-to-br from-[#0d0d0d] via-[#1f1a10] to-[#0d0d0d]" testid="asset-history" className="!border-0 !shadow-none" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
          {[
            { h: "Captions", tag: "SIMPLE + ADV", p: "Auto-generated, editable, multilingual." },
            { h: "Overlays", tag: "ADVANCED", p: "WCAG-tested preset styles on a timed track." },
            { h: "Audio", tag: "ADVANCED", p: "Per-section volume with explicit AI Enhanced label." },
            { h: "Chapters", tag: "ADVANCED", p: "AI-generated, manually overridable. One-click regenerate." },
            { h: "History", tag: "SIMPLE + ADV", p: "Named timestamped log. Step back to any point." },
            { h: "Library", tag: "SIMPLE + ADV", p: "Upload and drop media directly to the timeline." },
          ].map((p, i) => (
            <motion.div key={p.h} {...fadeUpSubtle(i * 0.04)} className="p-4 rounded-xl border border-[color:var(--nn-border)] hover:border-[color:var(--nn-accent)]/50 transition-colors duration-200">
              <div className="font-mono text-[10px] text-[color:var(--nn-accent-text)] mb-1.5">{p.tag}</div>
              <div className="font-display text-lg">{p.h}</div>
              <div className="text-xs text-[color:var(--nn-muted)] mt-1 leading-relaxed">{p.p}</div>
            </motion.div>
          ))}
        </div>

        <div className="rounded-3xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 p-6 md:p-8">
          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                The trust breakthrough
              </div>
              <div className="font-display text-3xl md:text-4xl leading-tight">Named History.</div>
            </div>
            <div className="md:col-span-7 text-[color:var(--nn-text)]/80 text-sm md:text-base leading-relaxed">
              Every action gets a human name and a timestamp — <em>Split at 04:12</em>, <em>Auto-caption rewrite</em>.
              Instructors stopped freezing and started exploring. In user testing, undo confidence became the single
              biggest predictor of feature adoption.
            </div>
          </div>
        </div>
      </Block>

      <Bridge label="what improved" />

      {/* 06 · Impact & Results — illustrated "What Improved" panel */}
      <Block
        id="cs-impact"
        idx={5}
        n="06"
        label="Impact & Results"
        sticky
        title={<>What actually <span className="font-serif-i text-[color:var(--nn-muted)]">shipped.</span></>}
        kicker="Shipped in 6 weeks across dark and light themes, WCAG AA in both. Now rolling into the 2026 institution-wide release."
      >
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-8 grid place-items-center rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)]">
              <IconSparkles size={14} strokeWidth={2} />
            </span>
            <div className="font-display text-2xl md:text-3xl">What Improved</div>
          </div>
          <p className="text-sm md:text-base text-[color:var(--nn-muted)] max-w-2xl leading-relaxed mb-6">
            Redesign led to faster task completion, smoother onboarding, and happier instructors — backed by six shipped metrics.
          </p>
          <motion.div {...staggerParent(0.05)} className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <MetricCard Icon={IconStackLg} tone="lime" value="1,000+" label="Institutions" sub="Live on the new editor across North America" delay={0} />
            <MetricCard Icon={IconTrendDown} tone="peach" value="−60%" label="Time to first edit" sub="Vs. legacy Simple task benchmarks" delay={0.05} />
            <MetricCard Icon={IconTrendUp} tone="blue" value="4→1" label="Panels for Simple mode" sub="Consolidated by task, not by feature" delay={0.1} />
            <MetricCard Icon={IconShield} tone="mint" value="AA" label="WCAG · both themes" sub="Verified with Axe DevTools" delay={0.15} />
            <MetricCard Icon={IconSparkles} tone="pink" value="1" label="Prototype replaced" sub="…multiple rounds of static spec docs" delay={0.2} />
            <MetricCard Icon={IconClockLg} tone="peach" value="6 wks" label="Design → handoff" sub="Concept to token-synced dev-ready" delay={0.25} />
          </motion.div>
        </div>

        <motion.div {...fadeUp(0)} className="p-6 md:p-8 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
            Qualitative wins
          </div>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm md:text-base text-[color:var(--nn-text)]/85">
            {[
              "TAs finished captioning without opening documentation.",
              "AI features are now discovered without a tooltip explaining them.",
              "Undo confidence stopped being the #1 support ticket theme.",
              "Design tokens synced 1:1 with engineering — zero last-mile drift.",
            ].map((t) => (
              <li key={t} className="flex gap-3 py-1">
                <span className="text-[color:var(--nn-accent-text)] shrink-0">→</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </Block>

      <Bridge label="what i'd do next" />

      {/* 07 · Reflection — no sticky notes next to screenshots */}
      <Block
        id="cs-reflection"
        idx={6}
        n="07"
        label="Reflection & Learnings"
        sticky
        title={<>What I'd do <span className="font-serif-i text-[color:var(--nn-muted)]">differently.</span></>}
        kicker="A designer's honesty test. The parts that landed, the parts I'd rebuild, and what I carry into the next brief."
      >
        {/* Prompt scaffold — clean solo screenshot moment */}
        <div className="mb-10">
          <div className="spotlight-frame">
            <BrowserFrame src={project.assets.aiPrompt} alt="Initial FigmaMake prompt and generated scaffold" ratio="16/9" url="figmamake.new/lumina-editor" background="bg-gradient-to-br from-[#0d0d0d] via-[#101c1a] to-[#0d0d0d]" testid="asset-ai-prompt" className="!border-0 !shadow-none" />
          </div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
            <span className="text-[color:var(--nn-accent-text)]">▚</span> The first prompt — five zones, two modes, six panels, tokens attached
          </div>
        </div>

        {/* Reflection list */}
        <ul className="space-y-1 mb-14">
          {[
            "Test with real post-secondary instructors at partner institutions — not internal proxies.",
            "Validate the Simple-mode task set with structured interviews, not audit telemetry alone.",
            "Scope the FERPA blur overlay into V1, not V2 — accessibility teams asked for it first.",
            "Build the AI label taxonomy as a design-system primitive. Don't rewrite it every release.",
          ].map((t, i) => (
            <motion.li
              key={i}
              {...fadeUpSubtle(i * 0.04)}
              className="flex gap-6 py-5 border-b border-[color:var(--nn-border)] group hover:pl-2 transition-[padding] duration-300"
            >
              <span className="font-mono text-xs text-[color:var(--nn-accent-text)] shrink-0 mt-1">0{i + 1}</span>
              <span className="text-lg md:text-xl text-[color:var(--nn-text)]/90 leading-snug group-hover:text-[color:var(--nn-text)] transition-colors">{t}</span>
            </motion.li>
          ))}
        </ul>

        {/* Closing quote */}
        <motion.blockquote
          {...fadeUp(0.05)}
          className="font-serif-i text-3xl md:text-5xl leading-tight max-w-3xl border-l-2 border-[color:var(--nn-accent)] pl-6"
        >
          "The best editor gets out of the way. This one finally started to."
        </motion.blockquote>
      </Block>
    </CaseStudyLayout>
  );
};

export default YujaLumina;
